// import icons
import { Zap } from "lucide-react";
import { ArrowRight, ArrowDown, ArrowLeftRight, ArrowUpDown } from "lucide-react";
// import data types
import { DataKeys, DataStructure } from "../../types";
// import components
import { MainFrame, ImageBannerFrame, SpacingFrame } from "./Frame";
import { ToolCard, ToolCard2, ToolCard3 } from "./Cards";
import { TitleSubtitle, TextBody, BannerText, TextBulletList } from "./TextFormat";
import { NumberCounterAnim } from "../tools/Animations";
import { MoreButton } from "../tools/Buttons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

// import image
import dotImage from "../../image/health/dot.svg";

// import context
import { useLang } from "../Context";
// import global var
import { maxWidthScreen } from "../GlobalVar";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";

import { useEffect, useRef } from "react";


interface BannerSectionProps {
    image: string;
    title: string;
    subtitle: string;
    textPosition: "Top" | "Bottom";
};
BannerSection.defaultProps = {
    textPosition: "Bottom"
};
export function BannerSection({ image, title, subtitle, textPosition } : BannerSectionProps) {
    return (
        <ImageBannerFrame image={image} height={460}>
            <div className="m-auto w-full lg:px-0 sm:px-8">
                <BannerText title={ title } subtitle={ subtitle } position={ textPosition } />
            </div>
        </ImageBannerFrame>
    )
};

interface MainIdeaSectionProps {
    text: string;
};
export function MainIdeaSection({ text } : MainIdeaSectionProps) {
    return (
        <MainFrame style={{ backgroundColor: "rgba(162, 218, 219, 0.4)" }}>
            <p className="text-left text-xl font-semibold">{ text }</p>
        </MainFrame>
    )
};

interface BusinessBannerSectionProps {
    bgImage: string;
    logoImage: string;
    title: string;
    subtitle: string;
};
export function BusinessBannerSection({ bgImage, logoImage, title, subtitle } : BusinessBannerSectionProps) {
    return (
        <ImageBannerFrame image={bgImage}>
            <div className="m-auto w-full lg:px-0 sm:px-8">
                <div className="flex gap-2 lg:flex-row sm:flex-col">
                    <img className="w-24" src={logoImage} alt="logo"/>
                    <div>
                        <p className="text-left lg:text-3xl sm:text-[44px] text-white font-bold sm:leading-none">{ title }</p>
                        <p className="text-left text-base text-white sm:mt-2">{ subtitle }</p>
                    </div>
                </div>
            </div>
        </ImageBannerFrame>
    )
};


interface BusinessUnitSectionProps {
    title: string;
    text: string[];
    bulletList?: string[];
    data: DataKeys;
};
export function BusinessUnitSection({ title, text, bulletList, data } : BusinessUnitSectionProps) {
    return (
        <MainFrame style={{ backgroundColor: "#A2DADB" }}>
            <div className="lg:grid lg:grid-cols-4 sm:flex sm:flex-col gap-10">
                <div className="col-span-3 flex my-auto">
                    <SpacingFrame>
                        <TitleSubtitle title={ title } left />
                        <TextBody text={ text } />
                        { bulletList && <TextBulletList list={ bulletList } /> }
                    </SpacingFrame>
                </div>
                <div className="col-span-1 mx-auto">
                    <ToolCard data={ data } />
                </div>
            </div>
        </MainFrame>
    )
};

interface BannerVideoSectionProps {
  videoSrc: string;
  title: string;
  subtitle: string;
  buttonLabel: string;
  buttonLink: string;
  darkMode?: boolean;
  loop?: boolean;
  playbackRate?: number
}

export function BannerVideoSection({
  videoSrc,
  title,
  subtitle,
  buttonLabel,
  buttonLink,
  darkMode,
  loop = true,
  playbackRate = 1
}: BannerVideoSectionProps) {

  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  
  return (
    <div className="relative w-full h-screen overflow-hidden">
      <video
        ref={videoRef}
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
        src={videoSrc}
        autoPlay
        muted
        playsInline
        {...(loop ? { loop: true } : {})}
      />

      <div className="relative z-10 flex items-center h-full">
      <motion.div
        key={location.pathname}
        initial={{ y: "20%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="m-auto w-full px-8 lg:px-0"
        style={{ maxWidth: `${maxWidthScreen}px` }}
      >
        <div className="w-full max-w-[80%] lg:max-w-[680px] flex flex-col justify-center h-full space-y-4 lg:space-y-8 " >
          <h1 className="text-left lg:text-3xl sm:text-[44px] font-bold leading-tight whitespace-pre-line ">{title}</h1>
          <p className="text-left text-lg sm:mt-2">{subtitle}</p>

          {buttonLabel && buttonLink && (
            <div className="w-fit">
              <a
                href={buttonLink}
                className={`group flex items-center justify-center gap-2 rounded-md text-base text-teal-500 px-3 py-1
                  ${darkMode ? "hover:bg-teal-500" : "hover:bg-teal-100/50"} hover:gap-4 transition-all duration-300`}
              >
                <p className={`text-teal-500 font-bold ${darkMode && "group-hover:text-white"}`}>
                  {buttonLabel}
                </p>
                <FontAwesomeIcon
                  className={`${darkMode && "group-hover:text-white"}`}
                  icon={faArrowRight}
                />
              </a>
            </div>
          )}
        </div>
        </motion.div>
      </div>
    </div>
  );
}

interface StatsSectionProps {
    title: string;
    subtitle: string;
    data: DataStructure;
    topIcon?: boolean;
}
export function StatsSection({ title, subtitle, data, topIcon=true } : StatsSectionProps) {
    const { lang } = useLang();

    let content = Object.keys(data).map((i) => {
        let contentData = data[i];
        return (
            <div className="flex flex-col gap-2">
                <p className="text-3xl text-teal-500 font-semibold">
                    <span>{ contentData["prefix"][lang] }</span>
                    <NumberCounterAnim value={ contentData["number"] } />
                    <span>{ contentData["title"][lang] }</span>
                </p>
                <p className="text-md font-medium">{ contentData["subtitle"][lang] }</p>
            </div>
        )
    });

    return (
        <SpacingFrame spacing={32} center>
            { topIcon && <div className="relative w-14 h-14 rounded-full bg-teal-100">
                <div className="absolute w-full h-full flex items-center justify-center">
                    <Zap className="text-teal-500" height={24} width={24} />
                </div>
            </div> }
            <TitleSubtitle title={ title } subtitle={ subtitle } spacing={22} />
            <div className="w-full lg:grid lg:grid-flow-col lg:auto-cols-auto lg:gap-4 lg:divide-x sm:flex sm:flex-col sm:gap-10">
                { content }
            </div>
        </SpacingFrame>
    )
}


interface ImpactStatsCardProps {
    title: string;
    subtitle: string;
    data: DataStructure;
  }

  export function ImpactStatsCard({ title, subtitle, data }: ImpactStatsCardProps) {
    const { lang } = useLang();
  
    let content = Object.keys(data).map((i) => {
      let contentData = data[i];
      return (
        <div key={i} className="flex flex-col gap-4 px-2">
          <p className="text-xl text-teal-500 font-bold text-center">
            <span>{contentData["prefix"][lang]}</span>
            <NumberCounterAnim value={contentData["number"]} />
            <span>{contentData["title"][lang]}</span>
          </p>
          <p className="text-md font-semibold text-neutral-800 text-center">
            {contentData["subtitle"][lang]}
          </p>
        </div>
      );
    });
  
    return (
      <SpacingFrame spacing={32} center>
        <div className="w-full rounded-3xl px-6 py-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between w-full gap-11">
            {/* Left section: Title and subtitle */}
            <div className="lg:w-1/2 w-full px-6 lg:pr-6">
              <TitleSubtitle
                title={title}
                subtitle={subtitle}
                spacing={22}
                left={true}
              />
            </div>
  
            {/* Right section: dynamic stats content */}
            <div className="w-full lg:w-1/2 grid grid-cols-1 lg:grid-cols-3 gap-10 px-4 justify-items-center lg:justify-items-start text-center lg:text-left self-center">


              {content}
            </div>
          </div>
        </div>
      </SpacingFrame>
    );
  }
  

interface Section3CardProps {
    title: string;
    subtitle?: string;
    capsText: string;
    data: DataStructure;
    icon?: string; 
}

export function Section3Card({ title, capsText, data, icon }: Section3CardProps) {
    const { lang } = useLang(); 

    const drugContent = Object.keys(data).map((i) => {
        let content = data[i];

        return (
            <div key={i} className="flex flex-col items-center lg:items-center gap-4">
                {content["icon"] && <img src={content["icon"]} className="w-12 h-12 mb-2" alt="" />}


                <p className="text-3xl text-teal-600 font-bold">
                    <NumberCounterAnim value={content["number"]} />
                    <span className="ml-1">{content["title"][lang]}</span>
                </p>
                <p className="text-md font-bold">{content["subtitle"][lang]}</p>
                <p className="text-sm text-grey-500">{content["text"][lang]}</p>
            </div>
        );
    });

    return (
        <SpacingFrame spacing={32} center>
            <div className="w-full bg-neutral-50 rounded-3xl px-10 py-12">
                {icon && <img src={icon} className="w-16 h-16 mb-4 mx-auto lg:mx-0" alt="Section Icon" />}

                <div className="text-center lg:text-left mb-8">
                    <p className="text-left text-base  text-grey-400">{capsText}</p>
                    <h2 className="text-left text-xl font-semibold">{title}</h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 text-center lg:text-center">
                    {drugContent}
                </div>
            </div>
        </SpacingFrame>
    );
}

interface StepSectionWithToolsProps {
  title: string;
  label: string;
  text: string;
  tools: DataStructure;
  reverse?: boolean;
}

export function StepSectionWithTools({
  title,
  label,
  text,
  tools,
  reverse = false,
}: StepSectionWithToolsProps) {

  const toolCount = Object.keys(tools).length;
  const gridColsClass =
  toolCount === 1 ? "lg:grid-cols-1" :
  toolCount === 2 ? "lg:grid-cols-2" :
  "lg:grid-cols-3"; 
  
  return (

    <div className="w-full px-12 py-12 space-y-12 my-16">
      {/* image + text */}
      <div className={`flex flex-col lg:flex-row ${reverse ? 'lg:flex-row-reverse' : ''} items-center lg:items-start gap-8 lg:gap-16`}>
        <div className={`w-full lg:w-1/3 flex ${reverse ? "justify-end" : "justify-start"}`}>
          <img src={dotImage} alt="" />
        </div>
        <div className={`w-full lg:w-2/3 flex flex-col space-y-8 ${reverse ? 'items-start text-left' : 'items-end text-right'}`}>
          <h2 className={`${reverse ? "text-left" : "text-right"} text-xl font-semibold whitespace-pre-line`}>
            {title}
          </h2>

          <div className="inline-block px-4 py-1 rounded-full bg-white text-base font-semibold">
            {label}
          </div>

          <p className={`${reverse ? "text-left" : "text-right"} text-base leading-relaxed`}>
            {text}
          </p>
        </div>
      </div>

      {/* ToolCards */}
      <div className={`${toolCount === 1 ? "flex justify-end" : `grid grid-cols-1 ${gridColsClass} gap-5`}`}>
        {Object.keys(tools).map((key) => (
          <div
            key={key}
            className={toolCount === 1 ? "w-full max-w-[478px]" : " w-full"} 
          >
            <ToolCard3
              data={tools[key]}
              isBulletList
              width="100%"
              height="auto"
              imagePath="image/icons/"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

interface ImageTextWithStepsProps {
  title: string;
  steps: {
    number: string;
    subtitle: string;
    text: string;
  }[];
  imagePaths: string[];
}

export function ImageTextWithSteps({ title, steps, imagePaths }: ImageTextWithStepsProps) {
  return (
      <div className="w-full flex flex-col lg:flex-row gap-16  ">
        {/* 左邊 */}
        <div className="w-full lg:w-1/2 flex  ">
          <div className="flex w-full flex-col lg:flex-row gap-2 ">
            {imagePaths.map((src, idx) => (
              <div key={idx} className="lg:flex-1 flex max-h-[120px] lg:max-h-none ">
                <img
                  src={require(`../../image/health/${src}`)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* 右邊 */}
        <div className="w-full lg:w-1/2 flex flex-col justify-between ">
        <h2 className="text-left text-4xl font-semibold whitespace-pre-line mb-12">
          {title}
        </h2>
        
        <div className="flex flex-col gap-4">
        {steps.map((step, idx) => (
          <div key={idx} className="flex flex-col gap-1">
            <p className="text-left font-semibold text-[20px]">
              {step.number}. {step.subtitle}
            </p>
            <p className="text-left text-grey-400 text-base whitespace-pre-line" >
              {step.text}
            </p>

            {idx !== Object.keys(steps).length - 1 && (
            <hr className="w-full"   
                style={{
              marginTop: "16px", 
              height: "0.5px",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              border: "none"
            }}/>
          )}

          </div>
        ))}
      </div>
      </div>
    </div>
  );
}


interface StepCardProps4 {
  imageSrc: string;
  title: string;
  text: string;
}

export function StepCard4({ imageSrc,  title, text }: StepCardProps4) {
  return (
    <div className="flex flex-col h-full items-start w-full max-w-[300px]">
      <img src={require(`../../image/health/${imageSrc}`)} alt="" className="w-full object-contain mb-4 max-h-[220px]" />
      <div className="mt-2 flex flex-col gap-2 ">
        <p className="text-left text-base font-semibold whitespace-pre-line">
          {title}
        </p>
        <p className="text-left text-grey-400 text-[14px]">
          {text}
        </p>
      </div>
    </div>
  );
}



interface Step {
  imageSrc: string;
  title: string;
  text: string;
}

interface ThreeStepsWithArrowsProps {
  steps: Step[];
}

export function ThreeStepsWithArrows({ steps }: ThreeStepsWithArrowsProps) {
  return (
    <div className="w-full flex flex-col items-center">
      <div className="flex flex-col lg:flex-row items-center justify-center">
        {steps.map((step, index) => (
          <div key={index} className="flex flex-col lg:flex-row items-center">
            <StepCard4
              imageSrc={step.imageSrc}
              title={step.title}
              text={step.text}
            />
            {index < steps.length - 1 && (
              <div className="py-10 lg:px-10 flex justify-center">
                {index === 0 ? (
                  <>
                    <ArrowRight className="hidden lg:block w-10 h-10" />
                    <ArrowDown className="block lg:hidden w-10 h-10" />
                  </>
                ) : (
                  <>
                    <ArrowLeftRight className="hidden lg:block w-10 h-10" />
                    <ArrowUpDown className="block lg:hidden w-10 h-10" />
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

